<template>
  <div class="d-flex flex-column">
    <div class="pa-0 text-black mb-4">
      {{ i18n.t('texts.invite_text_hint') }}
    </div>
    <v-form ref="inviteForm" @submit.prevent="sendInvitation">
      <EmailSuggestion
        :id="email"
        key="invitation-email-input"
        v-model.trim="email"
        autofocus
        data-test-id="forward-email"
        :label="i18n.t('labels.email')"
        :rules="[ruleEmail]"
      />
    </v-form>

    <v-textarea
      v-model.trim="message"
      class="mt-2 invite-form-message mb-0"
      density="compact"
      hide-details
      :label="i18n.t('labels.add_message')"
      no-resize
      :placeholder="i18n.t('placeholders.add_message')"
      variant="outlined"
    />

    <portal to="document-dialog-actions">
      <VBtnSecondary variant="outlined" @click="closeDialog"
        >{{ i18n.t('buttons.cancel') }}
      </VBtnSecondary>
      <VBtnPrimary
        color="primary"
        data-test-id="send-invitation"
        :disabled="!canSend"
        :loading="loading"
        @click="sendInvitation"
        >{{ i18n.t('buttons.send_invitation') }}
      </VBtnPrimary>
    </portal>
  </div>
</template>
<script setup lang="ts">
import EmailSuggestion from '@/components/inputs/Emailsuggestion/EmailSuggestion.vue';
import { useI18n } from 'vue-i18n';
import { computed, ref } from 'vue';
import { useSignStore } from '@/stores/sign.ts';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { useMessageStore } from '@/stores/message.ts';
import { useEmitter } from '@/composables/useEmitter';
import AuthService from '@/services/authService.ts';
import { useEmailService } from '@/components/inputs/Emailsuggestion/useEmailService';
import { EMAIL_REGEX, ruleEmail } from '@deepcloud/field-validators';
import { Portal } from 'portal-vue';

const i18n = useI18n();
const emit = defineEmits(['closeDialog']);

const { currentDocument } = useDocumentContext();
const emitter = useEmitter();
const { saveRecentlyUsedEmails } = useEmailService();

// Store
const signStore = useSignStore();
const messageStore = useMessageStore();

// FORM
const inviteForm = ref();
const email = ref<string | null>(null);
const message = ref<string | null>(null);
const loading = ref(false);

const canSend = computed(() => {
  return !!email.value && EMAIL_REGEX.test(email.value);
});

async function sendInvitation() {
  try {
    const isValid = inviteForm.value.validate();
    if (currentDocument.value && email.value && isValid) {
      loading.value = true;
      // Update preference with new emails
      if (AuthService.isAuthenticated) {
        try {
          await saveRecentlyUsedEmails([{ email: email.value }]);
        } catch (error) {
          console.error(error);
        }
      }
      await signStore.addNewSignee(currentDocument.value.documentId, {
        email: email.value,
        comment: message.value === '' ? null : message.value
      });
      messageStore.showMessage({
        key: 'snackbars.signee_added',
        color: 'success',
        icon: 'fa fa-circle-check'
      });

      closeDialog();
      emitter.$emit('update-sign-status');
    }
  } catch (error) {
    messageStore.showMessage({
      key: 'errors.error_occurred',
      color: 'error',
      icon: 'fa fa-circle-exclamation'
    });
  } finally {
    loading.value = false;
  }
}

function closeDialog() {
  email.value = null;
  message.value = null;
  emit('closeDialog');
}
</script>

<style scoped lang="scss">
:deep(.v-text-field.v-text-field--enclosed) .v-text-field__details {
  padding-left: 0 !important;
}

.v-input :deep(.v-input__details) {
  padding-left: 0%;
  padding-top: 8px;
  min-height: 18px;
}

.v-input.v-text-field {
  margin-bottom: 0.75rem;
}

.email-input :deep(.slide-y-transition-leave-active) {
  transition: none !important;
}

.email-input :deep(.slide-y-transition-leave-to) {
  transition: none !important;
}

.email-input :deep(.slide-y-transition-enter-active) {
  transition: none !important;
}
.email-input :deep(.slide-y-transition-enter-to) {
  transition: none !important;
}
.invite-form-message :deep(.v-input__details) {
  visibility: none;
}
</style>
